import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useAppSelector } from "src/store/actions/action";
import { setRedeemImageList, setFulFillId, setRedeemDataAllInit } from "src/store/reducers/redeemReduce";
import CheckBox from "src/components/common/CheckBox";
import LazyLoadImage from "src/components/common/LazyLoadImage";
import NoImagePlaceholder from "src/components/common/NoImagePlaceholder";
import iconCheck from "src/assets/images/iconCheck.svg";
import iconCheckActive from "src/assets/images/iconCheckActive.svg";
import cn from "classnames";
import styled from "styled-components";
import GalleryImageModal from "./GalleryImageModal";
import ScoreRegisterBox from "./ScoreRegisterBox";
import BottomModal, { RefProps } from "../common/BottomModal";
import Button from "../common/Button";
import { useAssessFulfillScoreMutation } from "../../api/fulfillAPI";
import { formatScore } from "src/utils/formatScore";
import { isAssessed } from "src/utils/isAssessed";

interface IFulfillItem {
  type?: string;
  data: any;
  selectedImages?: Set<string>;
  onImageSelect?: (imageId: string, isSelected: boolean, storeIdx: string) => void;
  selectedExecuteIds?: number[];
  onExecuteSelect?: (executeId: number) => void;
  onSelectAll?: (executeIds: number[]) => void;
  isAllSelected?: boolean;
}

interface SelectedImage {
  storeId: string;
  executeIdx: string;
  images: any[];
}

// 이미지 타입 정의
interface ExecuteImage {
  execute_img_idx: number;
  img_name?: string;
  img_origin_name?: string;
  img_url: string;
  ext?: string;
  type?: string;
}

// 이행 아이템 타입 정의
interface FulfillItem {
  execute_idx: number;
  execute_imgs: ExecuteImage[];
  store_name: string;
  store_id: string;
  store_type_name: string;
  execute_yn: "Y" | "N";
  re_execute_yn: "Y" | "N";
  assess_yn: "Y" | "N";
  score: number | null;
  assess_contents?: string;
  comment_count: number;
  comment_read_yn: "Y" | "N";
  insert_date: string;
}

const FulFillShopItem = (props: IFulfillItem) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const imageServerIp = process.env.REACT_APP_IMAGE_SERVER_IP;
  const [listData, setListData] = useState<FulfillItem[]>([]);
  const [selectedImages, setSelectedImages] = useState<SelectedImage | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImages, setModalImages] = useState<ExecuteImage[]>([]);
  const [modalInitialIndex, setModalInitialIndex] = useState(0);
  const [modalStoreItem, setModalStoreItem] = useState<FulfillItem | null>(null);
  const selectedReduxImages = useAppSelector((state) => state.redeemReduce.imageList);
  const userInfo = useAppSelector((state) => state.userReduce.userInfo);
  const assessData = useAppSelector((state) => state.fulfillDetailReduce.assessData);
  const selectedExecuteIds = useAppSelector((state) => state.fulfillListReduce.selectedExecuteIds) || [];

  // 평가 모달 관련 상태
  const [scoreModalOpen, setScoreModalOpen] = useState(false);
  const [currentAssessItem, setCurrentAssessItem] = useState<FulfillItem | null>(null);
  const scoreModalRef = React.useRef<RefProps>(null);

  // 평가 API
  const [insertAssess, { isLoading: isAssessing, isSuccess: assessSuccess }] = useAssessFulfillScoreMutation();

  // Create a memoized Set for O(1) lookup performance
  const selectedImageSet = useMemo(() => {
    const set = new Set<string>();
    selectedReduxImages.forEach((img: any) => {
      if (img.img_url && img.execute_idx) {
        set.add(`${img.img_url}_${img.execute_idx}`);
      }
    });
    return set;
  }, [selectedReduxImages]);

  useEffect(() => {
    if (props.data) {
      setListData(props.data.data);
    }
  }, [props.data]);

  const moveToDetail = (execute_yn: string, re_execute_yn: string, execute_idx: string) => {
    if (execute_yn === "Y" || re_execute_yn === "Y") {
      navigate(`/fulfill-detail/` + execute_idx);
    } else {
      alert("아직 이행하지 않은 매장입니다.");
    }
  };

  const handleImageSelect = (storeItem: any, image: any) => {
    // 재이행 매장은 선택 불가
    if (storeItem.re_execute_yn === "Y") {
      alert("재이행 매장의 이미지는 선택할 수 없습니다.");
      return;
    }

    // 재이행 이미지나 완료 이미지는 선택 불가
    if (image.type !== null) {
      alert("해당 이미지는 선택할 수 없습니다.");
      return;
    }

    const imageWithStoreInfo = {
      ...image,
      store_id: storeItem.store_id,
      store_name: storeItem.store_name,
      execute_idx: storeItem.execute_idx,
    };

    // 이미 선택된 이미지인지 확인
    const isSelected = selectedImageSet.has(`${image.img_url}_${storeItem.execute_idx}`);

    if (isSelected) {
      // 선택 해제
      const filteredImages = selectedReduxImages.filter((img: any) => 
        !(img.img_url === image.img_url && (img as any).execute_idx === storeItem.execute_idx)
      );
      dispatch(setRedeemImageList({ imageList: filteredImages }));
      
      // 모든 이미지가 해제되면 selectedImages도 초기화
      if (filteredImages.length === 0) {
        setSelectedImages(null);
        dispatch(setFulFillId({ fulfillId: "" }));
      }
    } else {
      // 다른 매장의 이미지가 이미 선택되어 있는지 확인
      if (selectedReduxImages.length > 0 && (selectedReduxImages[0] as any).execute_idx !== storeItem.execute_idx) {
        if (window.confirm("같은 매장의 이미지만 선택 가능합니다\n변경하시겠습니까?")) {
          // 기존 선택 초기화하고 새로 선택
          dispatch(setRedeemDataAllInit());
          dispatch(setRedeemImageList({ imageList: [imageWithStoreInfo] }));
          dispatch(setFulFillId({ fulfillId: storeItem.execute_idx }));
          setSelectedImages({
            storeId: storeItem.store_id,
            executeIdx: storeItem.execute_idx,
            images: [imageWithStoreInfo],
          });
        }
      } else {
        // 같은 매장의 이미지 추가
        dispatch(setRedeemImageList({ imageList: [...selectedReduxImages, imageWithStoreInfo] }));
        dispatch(setFulFillId({ fulfillId: storeItem.execute_idx }));
        setSelectedImages({
          storeId: storeItem.store_id,
          executeIdx: storeItem.execute_idx,
          images: [...selectedReduxImages, imageWithStoreInfo],
        });
      }
    }
  };

  const isImageSelected = (storeItem: any, image: any) => {
    return selectedImageSet.has(`${image.img_url}_${storeItem.execute_idx}`);
  };

  const canSelectImage = (storeItem: any, image: any) => {
    // 재이행 선택 가능 조건:
    // 1. 본사 직원만 재이행 요청 가능
    // 2. 이행이 완료된 상태
    // 3. 평가가 미완료 상태
    // 4. 재이행 중이 아님
    // 5. 일반 이미지 (재이행/완료 이미지가 아님)
    return (
      userInfo.user_type === "E" &&              // 본사 직원만
      storeItem.execute_yn === "Y" &&            // 이행 완료
      storeItem.assess_yn === "N" &&             // 평가 미완료
      storeItem.re_execute_yn !== "Y" &&         // 재이행 중이 아님
      image.type === null                        // 일반 이미지
    );
  };

  const handleImageClick = (storeItem: any, imageIndex: number) => {
    if (props.type === "gallery") {
      setModalImages(storeItem.execute_imgs);
      setModalInitialIndex(imageIndex);
      setModalStoreItem(storeItem);
      setModalOpen(true);
    }
  };

  const handleModalImageSelect = (image: any, storeItem: any) => {
    handleImageSelect(storeItem, image);
  };

  // 갤러리 이미지 영역 마우스 드래그 가로 스크롤 (모바일 스와이프처럼)
  const dragRef = React.useRef({ isDown: false, startX: 0, scrollLeft: 0, moved: false });
  const handleDragStart = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    dragRef.current = { isDown: true, startX: e.pageX, scrollLeft: el.scrollLeft, moved: false };
    el.style.cursor = "grabbing";
  };
  const handleDragMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!dragRef.current.isDown) return;
    e.preventDefault();
    const dx = e.pageX - dragRef.current.startX;
    if (Math.abs(dx) > 4) dragRef.current.moved = true;
    e.currentTarget.scrollLeft = dragRef.current.scrollLeft - dx;
  };
  const handleDragEnd = (e: React.MouseEvent<HTMLDivElement>) => {
    dragRef.current.isDown = false;
    e.currentTarget.style.cursor = "";
  };
  // 드래그한 경우 이미지 클릭(모달 열기)을 막음
  const handleDragClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    if (dragRef.current.moved) {
      e.stopPropagation();
      dragRef.current.moved = false;
    }
  };

  const handleRedeemRequest = () => {
    if (selectedReduxImages.length === 0) {
      alert("이미지를 선택해주세요.");
      return;
    }
    navigate("/redeem-request");
  };

  // 체크박스 선택 핸들러
  const handleExecuteSelect = (executeId: number) => {
    if (props.onExecuteSelect) {
      props.onExecuteSelect(executeId);
    }
  };

  // 선택된 상태 확인
  const isExecuteSelected = (executeId: number) => {
    return selectedExecuteIds.includes(executeId);
  };

  // 재이행 완료 여부 확인 헬퍼 함수
  const isRedeemCompleted = (image: ExecuteImage) => image.type === "C" || image.type === null;

  // 미완료 재이행 개수 확인 헬퍼 함수
  const getIncompleteRedeemCount = (item: FulfillItem) => {
    return item.execute_imgs ? item.execute_imgs.filter(img => img.type !== null && img.type !== "C").length : 0;
  };

  // 평가 권한 체크 함수
  const canAssess = (item: FulfillItem) => {
    // 본사 직원만 평가 가능
    if (userInfo.user_type !== "E") return false;

    // 이행이 완료되어야 함
    if (item.execute_yn !== "Y") return false;

    // 이미 평가된 경우에도 재평가 가능 (별점 재등록)
    if (item.score !== null) return true;

    // 재이행이 없는 경우 바로 평가 가능
    if (item.re_execute_yn === "N") return true;

    // 재이행이 있는 경우 모든 재이행이 완료되어야 평가 가능
    if (item.re_execute_yn === "Y") {
      return item.execute_imgs && item.execute_imgs.every(isRedeemCompleted);
    }

    return false;
  };

  // 별점 클릭 핸들러
  const handleStarClick = (item: FulfillItem) => {
    if (!canAssess(item)) {
      if (userInfo.user_type !== "E") {
        alert("평가 권한이 없습니다.");
      } else if (item.execute_yn !== "Y") {
        alert("이행이 완료되지 않았습니다.");
      } else if (item.re_execute_yn === "Y") {
        const redeemCount = getIncompleteRedeemCount(item);
        if (redeemCount > 0) {
          alert("재이행이 완료되지 않았습니다.");
        } else {
          setCurrentAssessItem(item);
          if (scoreModalRef.current) scoreModalRef.current.modalOpen();
        }
      }
      return;
    }

    setCurrentAssessItem(item);
    if (scoreModalRef.current) scoreModalRef.current.modalOpen();
  };

  // 평가 등록/수정
  const setAssessScore = async () => {
    if (!currentAssessItem) return;

    if (assessData.comment === "" && assessData.score === 0) {
      alert("별점 0점일 경우 코멘트 작성은 필수입니다.");
      return;
    }

    const data = {
      execute_idx: currentAssessItem.execute_idx,
      score: assessData.score,
      assess_contents: assessData.comment,
    };

    try {
      const result = await insertAssess(data).unwrap();

      // 평가 성공 시 해당 아이템 업데이트 - 성능 최적화된 업데이트
      const idx = listData.findIndex((item: FulfillItem) => item.execute_idx === currentAssessItem.execute_idx);
      let updatedListData = listData;
      if (idx !== -1) {
        updatedListData = [
          ...listData.slice(0, idx),
          {
            ...listData[idx],
            score: assessData.score,
            assess_yn: "Y" as const,
            assess_contents: assessData.comment
          },
          ...listData.slice(idx + 1)
        ];
      }

      setListData(updatedListData);
      setCurrentAssessItem({
        ...currentAssessItem,
        score: assessData.score,
        assess_yn: "Y",
        assess_contents: assessData.comment
      });

      if (scoreModalRef.current) scoreModalRef.current.modalClose();
    } catch (error) {
      console.error("평가 등록 실패:", error);
      alert("평가 등록에 실패했습니다.");
    }
  };

  return (
    <>
      {listData && listData.length > 0 ? (
        <>
          {props.type === "list" ? (
            listData.map((v: any, i: number) => {
              return (
                <TypeItem key={i}>
                  <ShopItemSet>
                    <SelectCheckbox onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                      <CheckBox
                        id={`select-list-${v.execute_idx}`}
                        type="round"
                        checked={isExecuteSelected(v.execute_idx)}
                        onChange={() => handleExecuteSelect(v.execute_idx)}
                      />
                    </SelectCheckbox>
                    <div className="title" onClick={() => moveToDetail(v.execute_yn, v.re_execute_yn, v.execute_idx)}>
                      <span className="cate">{v.store_type_name}</span>
                      <span className={cn("name", { dimmed: v.execute_yn === "N" })}>{v.store_name}</span>
                      <span className="code">{v.store_id}</span>
                      <span className={cn("chat", { new: v.comment_read_yn === "N" })}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                          <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.4A8 8 0 1 1 21 12z" />
                        </svg>
                        {v.comment_count}
                      </span>
                    </div>
                    <div className="score-area">
                      {v.re_execute_yn === "Y" && <span className="remark-re">재이행</span>}
                      {isAssessed(v) && <span className="remark-assessed">평가 완료</span>}
                      {v.score > 0 ? (
                        <span className="star-score active">{v.score}</span>
                      ) : (
                        <span className="star-score">{formatScore(v.score)}</span>
                      )}
                    </div>
                  </ShopItemSet>
                </TypeItem>
              );
            })
          ) : props.type === "grid" ? (
            <>
              {listData.map((v: any, i: number) => {
                return (
                  <TypeItem key={i}>
                    <ShopItemGridSet>
                      <div className="img-wrap">
                        {/* 선택 체크박스 오버레이 - 대표 이미지 좌상단 (리스트/갤러리와 동일 로직 재사용) */}
                        <GridSelectCheckbox onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                          <CheckBox
                            id={`select-grid-${v.execute_idx}`}
                            type="round"
                            checked={isExecuteSelected(v.execute_idx)}
                            onChange={() => handleExecuteSelect(v.execute_idx)}
                          />
                        </GridSelectCheckbox>
                        <div
                          className="img-inner"
                          onClick={() => moveToDetail(v.execute_yn, v.re_execute_yn, v.execute_idx)}
                        >
                          {v.execute_imgs.length > 0 ? (
                            <LazyLoadImage
                              src={imageServerIp + v.execute_imgs[0].img_url}
                              alt={v.store_name}
                              width="100%"
                              height="100%"
                              objectFit="cover"
                            />
                          ) : (
                            <NoImagePlaceholder iconSize={36} radius={8} />
                          )}
                          {v.execute_imgs.length > 1 && <span className="extra-img">+{v.execute_imgs.length - 1}</span>}
                          {v.re_execute_yn === "Y" && <span className="remark-re">재이행</span>}
                        </div>
                      </div>
                      <div
                        className="grid-info"
                        onClick={() => moveToDetail(v.execute_yn, v.re_execute_yn, v.execute_idx)}
                      >
                        <div className="sub-info">
                          <span className="cate">{v.store_type_name}</span>
                          <span className="mark-area">
                            {isAssessed(v) && <span className="remark-assessed">평가 완료</span>}
                            {v.score > 0 ? (
                              <span className="star-score active">{v.score}</span>
                            ) : v.execute_yn === "Y" ? (
                              <span className="star-score">{formatScore(v.score)}</span>
                            ) : null}
                          </span>
                        </div>
                        <div className="name-row">
                          <span className={cn("name", { dimmed: v.execute_yn === "N" && v.re_execute_yn === "N" })}>{v.store_name}</span>
                          <span className={cn("chat", { new: v.comment_read_yn === "N" })}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                              <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.4A8 8 0 1 1 21 12z" />
                            </svg>
                            {v.comment_count}
                          </span>
                        </div>
                      </div>
                    </ShopItemGridSet>
                  </TypeItem>
                );
              })}
            </>
          ) : props.type === "gallery" ? (
            <>
              {listData.map((v: any, i: number) => {
                const currentStoreItem = v;
                return (
                  <GalleryItem key={i}>
                    <GalleryShopInfo>
                      <div style={{ display: "flex", alignItems: "center", flex: 1, gap: "8px", minWidth: 0 }}>
                        <SelectCheckbox onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                          <CheckBox
                            id={`select-${v.execute_idx}`}
                            type="round"
                            checked={isExecuteSelected(v.execute_idx)}
                            onChange={() => handleExecuteSelect(v.execute_idx)}
                          />
                        </SelectCheckbox>
                        <div className="title" onClick={() => moveToDetail(v.execute_yn, v.re_execute_yn, v.execute_idx)} style={{ flex: 1 }}>
                          <span className="cate">{v.store_type_name}</span>
                          <span className={cn("name", { dimmed: v.execute_yn === "N" })}>{v.store_name}</span>
                          <span className="code">{v.store_id}</span>
                          <span className={cn("chat", { new: v.comment_read_yn === "N" })}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                              <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.4A8 8 0 1 1 21 12z" />
                            </svg>
                            {v.comment_count}
                          </span>
                        </div>
                      </div>
                      <div>
                        {v.re_execute_yn === "Y" && <span className="remark-re">재이행</span>}
                        {isAssessed(v) && <span className="remark-assessed">평가 완료</span>}
                        {v.score > 0 ? (
                          <span
                            className="star-score active clickable"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStarClick(v);
                            }}
                          >
                            {v.score}
                          </span>
                        ) : (
                          <span
                            className="star-score clickable"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStarClick(v);
                            }}
                          >
                            {formatScore(v.score)}
                          </span>
                        )}
                      </div>
                    </GalleryShopInfo>
                    {v.execute_imgs.length > 0 && (
                      <GalleryImageContainer
                        onMouseDown={handleDragStart}
                        onMouseMove={handleDragMove}
                        onMouseUp={handleDragEnd}
                        onMouseLeave={handleDragEnd}
                        onClickCapture={handleDragClickCapture}
                      >
                        <GalleryImageScroll>
                          {v.execute_imgs.map((img: any, imgIndex: number) => (
                            <GalleryImageItem key={imgIndex}>
                              <div className="image-wrapper" onClick={() => handleImageClick(v, imgIndex)}>
                                <LazyLoadImage
                                  src={imageServerIp + img.img_url}
                                  alt={`이미지 ${imgIndex + 1}`}
                                  width="100%"
                                  height="100%"
                                  objectFit="cover"
                                />
                                {canSelectImage(v, img) && (
                                  <button
                                    className={cn("select-btn", { selected: isImageSelected(v, img) })}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleImageSelect(v, img);
                                    }}
                                  >
                                    {isImageSelected(v, img) ? (
                                      <img src={iconCheckActive} alt="선택됨" />
                                    ) : (
                                      <img src={iconCheck} alt="선택" />
                                    )}
                                  </button>
                                )}
                              </div>
                            </GalleryImageItem>
                          ))}
                        </GalleryImageScroll>
                      </GalleryImageContainer>
                    )}
                  </GalleryItem>
                );
              })}
            </>
          ) : null}
        </>
      ) : (
        <NoResult>리스트 결과가 없습니다.</NoResult>
      )}
      
      {props.type === "gallery" && modalOpen && (
        <GalleryImageModal
          images={modalImages}
          initialIndex={modalInitialIndex}
          onClose={() => setModalOpen(false)}
          onSelect={handleModalImageSelect}
          isSelected={isImageSelected}
          canSelect={canSelectImage}
          storeItem={modalStoreItem}
          imageServerIp={imageServerIp || ""}
        />
      )}

      {props.type === "gallery" && selectedReduxImages.length > 0 && (
        <RedeemRequestButton onClick={handleRedeemRequest}>
          재이행 ({selectedReduxImages.length})
        </RedeemRequestButton>
      )}

      {/* 평가 모달 */}
      <BottomModal
        ref={scoreModalRef}
        onClick2={setAssessScore}
        modalTitle={currentAssessItem && currentAssessItem.score !== null ? "별점 재등록" : "별점 등록"}
        footerBtn="등록"
        dimClickFun={() => {
          if (scoreModalRef.current) scoreModalRef.current.modalClose();
        }}
      >
        {currentAssessItem && (
          <ScoreRegisterBox
            star={currentAssessItem.score || 0}
            value={currentAssessItem.assess_contents || ""}
          />
        )}
      </BottomModal>
    </>
  );
};

const TypeItem = styled.li`
  .remark-re {
    display: inline-flex;
    align-items: center;
    padding: 4px 8px;
    background-color: #14284d;
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    border-radius: 4px;
  }
  .remark-assessed {
    display: inline-flex;
    align-items: center;
    padding: 4px 8px;
    background-color: #eaf1fb;
    color: #2f6fd0;
    font-size: 12px;
    font-weight: 600;
    border-radius: 4px;
  }
  .star-score {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-family: "Poppins";
    font-weight: 600;
    color: #2a2d31;
    &:before {
      content: "★";
      font-size: 15px;
      color: #cbd0d6;
    }
    &.active:before {
      color: #2f6fd0;
    }
    &.clickable {
      cursor: pointer;
      &:hover {
        opacity: 0.8;
      }
    }
  }
`;

const ShopItemSet = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 0;
  border-bottom: 1px solid ${({ theme }) => theme.color.linePrimary};
  .score-area {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .title {
    flex: 1;
    min-width: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    line-height: 1;
    .cate {
      position: relative;
      flex-shrink: 0;
      padding-right: 8px;
      margin-right: 8px;
      color: #9aa1ab;
      font-size: 14px;
      font-weight: 400;
      line-height: 1;
      &:after {
        content: "";
        position: absolute;
        top: 50%;
        right: 0;
        transform: translateY(-50%);
        width: 1px;
        height: 10px;
        background: #dcdfe3;
      }
    }
    .name {
      min-width: 0;
      flex-shrink: 1;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      font-size: 15px;
      font-weight: 500;
      &.dimmed {
        color: ${({ theme }) => theme.color.quaternary};
      }
    }
    .code {
      flex-shrink: 0;
      margin-left: 8px;
      color: #b3b8be;
      font-size: 13px;
    }
    .chat {
      flex-shrink: 0;
      margin-left: 12px;
      display: inline-flex;
      align-items: center;
      gap: 3px;
      color: #b3b8be;
      font-family: "Poppins";
      font-size: 13px;
      font-weight: 400;
      &.new {
        color: ${({ theme }) => theme.color.subMain};
      }
    }
  }
`;

const ShopItemGridSet = styled.div`
  .img-wrap {
    position: relative;
    overflow: hidden;
    max-width: 100%;
    padding-top: 100%;
    margin-bottom: 12px;
    border-radius: 8px;
    background: #f4f4f4;

    .img-inner {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      cursor: pointer;
    }

    .extra-img {
      z-index: 2;
      position: absolute;
      right: 0;
      bottom: 0;
      min-width: 44px;
      height: 28px;
      padding: 0 10px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: rgba(20, 40, 77, 0.72);
      color: #fff;
      font-family: "Poppins";
      font-size: 13px;
      font-weight: 600;
      border-top-left-radius: 8px;
    }

    .remark-re {
      z-index: 2;
      position: absolute;
      top: 8px;
      right: 8px;
    }
  }

  .grid-info {
    cursor: pointer;

    .sub-info {
      display: flex;
      align-items: center;
      justify-content: space-between;
      .cate {
        color: #9aa1ab;
        font-size: 13px;
        font-weight: 500;
      }
      .mark-area {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
    }

    .name-row {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-top: 4px;
      .name {
        min-width: 0;
        flex: 1;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        font-size: 15px;
        font-weight: 500;
        &.dimmed {
          color: ${({ theme }) => theme.color.quaternary};
        }
      }
      .chat {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        gap: 3px;
        color: #b3b8be;
        font-family: "Poppins";
        font-size: 13px;
        font-weight: 400;
        &.new {
          color: ${({ theme }) => theme.color.subMain};
        }
      }
    }
  }
`;

const GridSelectCheckbox = styled.div`
  z-index: 3;
  position: absolute;
  top: 8px;
  left: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.85);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);

  > span {
    display: inline-flex;
    align-items: center;
  }
  label {
    display: inline-flex !important;
    align-items: center;
    height: 24px !important;
    padding: 0 !important;
    line-height: 1 !important;
  }
  label > svg {
    position: static !important;
    transform: none !important;
    width: 24px !important;
    height: 24px !important;
  }
`;

const NoResult = styled.li`
  width: 100% !important;
  margin: 80px 0 40px;
  text-align: center;
`;

const GalleryItem = styled.li`
  width: 100%;
  padding: 0;
  margin-bottom: 24px;
  border-bottom: 1px solid ${({ theme }) => theme.color.linePrimary};
  position: relative;
  &:last-child {
    border-bottom: none;
  }
`;

const SelectCheckbox = styled.div`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  margin: 0;
  padding: 0;
  ${/* 공용 CheckBox의 절대배치 아이콘을 일반 흐름 flex 중앙 정렬로 보정 */ ""}
  > span {
    display: inline-flex;
    align-items: center;
  }
  label {
    display: inline-flex !important;
    align-items: center;
    height: 22px !important;
    padding: 0 !important;
    line-height: 1 !important;
  }
  label > svg {
    position: static !important;
    transform: none !important;
    width: 22px !important;
    height: 22px !important;
  }
`;

const GalleryShopInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  
  .title {
    min-width: 0;
    flex: 1;
    display: flex;
    align-items: center;
    cursor: pointer;
    line-height: 1;

    .cate {
      position: relative;
      flex-shrink: 0;
      padding-right: 8px;
      margin-right: 8px;
      color: #9aa1ab;
      font-size: 14px;
      font-weight: 400;
      line-height: 1;
      &:after {
        content: "";
        position: absolute;
        top: 50%;
        right: 0;
        transform: translateY(-50%);
        width: 1px;
        height: 10px;
        background: #dcdfe3;
      }
    }

    .name {
      min-width: 0;
      flex-shrink: 1;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      font-size: 15px;
      font-weight: 500;
      &.dimmed {
        color: ${({ theme }) => theme.color.quaternary};
      }
    }

    .code {
      flex-shrink: 0;
      margin-left: 8px;
      color: #b3b8be;
      font-size: 13px;
    }

    .chat {
      flex-shrink: 0;
      margin-left: 12px;
      display: inline-flex;
      align-items: center;
      gap: 3px;
      color: #b3b8be;
      font-family: "Poppins";
      font-size: 13px;
      font-weight: 400;
      &.new {
        color: ${({ theme }) => theme.color.subMain};
      }
    }
  }

  > div:last-child {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 8px;

    .remark-re {
      display: inline-flex;
      align-items: center;
      padding: 4px 8px;
      background-color: #14284d;
      color: #fff;
      font-size: 12px;
      font-weight: 600;
      border-radius: 4px;
    }

    .remark-assessed {
      display: inline-flex;
      align-items: center;
      padding: 4px 8px;
      background-color: #eaf1fb;
      color: #2f6fd0;
      font-size: 12px;
      font-weight: 600;
      border-radius: 4px;
    }

    .star-score {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-family: "Poppins";
      font-weight: 600;
      color: #2a2d31;
      &:before {
        content: "★";
        font-size: 15px;
        color: #cbd0d6;
      }
      &.active:before {
        color: #2f6fd0;
      }
      &.clickable {
        cursor: pointer;
        &:hover {
          opacity: 0.8;
        }
      }
    }
  }
`;

const GalleryImageContainer = styled.div`
  padding: 16px 0;
  min-width: 0;
  max-width: 100%;
  overflow-x: auto;
  cursor: grab;
  user-select: none;
  img {
    -webkit-user-drag: none;
  }
  scrollbar-width: thin;
  &::-webkit-scrollbar {
    height: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: #c4c9cf;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const GalleryImageScroll = styled.div`
  display: flex;
  gap: 10px;
  width: max-content;
  padding-bottom: 8px;
`;

const GalleryImageItem = styled.div`
  flex: 0 0 auto;

  .image-wrapper {
    position: relative;
    height: 300px;
    min-width: 160px;
    border-radius: 8px;
    overflow: hidden;
    background: repeating-linear-gradient(45deg, #f1f2f4 0 11px, #f6f7f8 11px 22px);

    > div {
      width: max-content;
      height: 100%;
      background: transparent;
    }

    img {
      width: auto;
      height: 100%;
      max-width: none;
      object-fit: contain;
      cursor: pointer;
    }
    
    .select-btn {
      position: absolute;
      top: 8px;
      right: 8px;
      width: 24px;
      height: 24px;
      padding: 0;
      border: none;
      background: transparent;
      cursor: pointer;
      z-index: 1;
      
      img {
        width: 100%;
        height: 100%;
      }
      
      &.selected {
        opacity: 1;
      }
    }
  }
`;

const RedeemRequestButton = styled.button`
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  padding: 12px 32px;
  background-color: ${({ theme }) => theme.color.main};
  color: white;
  border: none;
  border-radius: 24px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  z-index: 100;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  
  &:hover {
    opacity: 0.9;
  }
`;

export default FulFillShopItem;
